import React, { useEffect, useState } from 'react'
import { authClient } from '../lib/auth-client'
import { userService, companyService, type User, type Company } from '../services'
import { authService } from '../services/auth.service'
import { AuthCtx, type Role } from './auth.context'

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User>()
  const [company, setCompany] = useState<Company>()
  const [isLoading, setIsLoading] = useState(true)

  const loadUserAndCompany = async (userId: string) => {
    try {
      const userResult = await userService.getUserById(userId)
      setUser(userResult)

      if (userResult.companyId) {
        try {
          const companyResult = await companyService.getCompanyById(userResult.companyId)
          setCompany(companyResult)
        } catch (e) {
          console.warn('Company fetch failed:', e)
        }
      } else {
        setCompany(undefined)
      }

      return userResult
    } catch (error) {
      console.error('Error loading user data:', error)
      setUser(undefined)
      setCompany(undefined)
      return undefined
    }
  }

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const session = await authService.getSession()
        if (session?.user?.id) {
          await loadUserAndCompany(session.user.id)
        }
      } catch (error) {
        console.error('Error bootstrapping auth session:', error)
      } finally {
        setIsLoading(false)
      }
    }

    bootstrap()
  }, [])

  const login = async (
    email: string,
    password: string
  ): Promise<{ mfaRequired: true } | { mfaRequired: false; role?: Role }> => {
    const result = await authService.adminLogin({ email, password })
    const loginResult = result as { twoFactorRedirect?: boolean } | null

    if (loginResult?.twoFactorRedirect) {
      return { mfaRequired: true }
    }

    const session = await authService.getSession()
    const loaded = session?.user?.id ? await loadUserAndCompany(session.user.id) : undefined
    return { mfaRequired: false, role: loaded?.role }
  }

  const mfaVerify = async (_challengeId: string, code: string): Promise<Role | undefined> => {
    await authService.mfaVerify(code)

    const session = await authService.getSession()
    const loaded = session?.user?.id ? await loadUserAndCompany(session.user.id) : undefined
    return loaded?.role
  }

  const sendOtp = async (phone: string) => {
    await authService.employeeOtpRequest(phone)
  }

  const verifyOtp = async (phone: string, code: string) => {
    await authService.employeeOtpVerify(phone, code)
    const session = await authService.getSession()
    if (session?.user?.id) {
      await loadUserAndCompany(session.user.id)
    }
  }

  const logout = () => {
    authService.logout().catch(() => {})
    authClient.signOut()
    setUser(undefined)
    setCompany(undefined)
  }

  return (
    <AuthCtx.Provider
      value={{
        user,
        company,
        isLoading,
        login,
        mfaVerify,
        sendOtp,
        verifyOtp,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  )
}
